import type { ReactNode } from 'react';

/**
 * DataTable — admin 데이터 표. Studio Linen 밀집 변주.
 * 헤더 = mono 대문자 라벨, hairline 구분, 행 hover/selected(terracotta tint).
 * 로딩=Skeleton 행, 빈=empty 슬롯. 모바일=가로 스크롤.
 */
export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  loading?: boolean;
  skeletonRows?: number;
  empty?: ReactNode;
  className?: string;
}

export function DataTable<T>({
  columns, rows, rowKey, onRowClick, selectedKey,
  loading = false, skeletonRows = 6, empty, className,
}: DataTableProps<T>) {
  const cn = ['bq-table-wrap', className ?? ''].filter(Boolean).join(' ');

  return (
    <div className={cn}>
      <table className="bq-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{ width: c.width, textAlign: c.align ?? 'left' }}
                className="bq-table__th"
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: skeletonRows }).map((_, i) => (
              <tr key={`sk-${i}`} className="bq-table__row bq-table__row--skeleton">
                {columns.map((c) => (
                  <td key={c.key} className="bq-table__td">
                    <span className="skeleton skeleton-line" />
                  </td>
                ))}
              </tr>
            ))}

          {!loading &&
            rows.map((row) => {
              const k = rowKey(row);
              const selected = selectedKey != null && k === selectedKey;
              return (
                <tr
                  key={k}
                  className={`bq-table__row${onRowClick ? ' is-clickable' : ''}${selected ? ' is-selected' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } }
                      : undefined
                  }
                  aria-selected={onRowClick ? selected : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{ textAlign: c.align ?? 'left' }}
                      className={`bq-table__td${c.mono ? ' is-mono' : ''}`}
                    >
                      {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>

      {!loading && rows.length === 0 && (
        <div className="bq-table__empty">{empty ?? 'No data'}</div>
      )}
    </div>
  );
}
